/**
 * Environment Configuration
 * Centralized environment and app configuration
 */

/**
 * Per-tenant flag helpers — subdomain-derived at runtime so no build-arg
 * plumbing needed for simple tenant toggles. Uses the first hostname
 * segment: `sudama.basiq360.com` → 'sudama', `oreno.basiq360.tech` → 'oreno'.
 * Returns null on localhost / IP / one-segment hostnames.
 */
export function getTenantSubdomain(): string | null {
  if (typeof window === 'undefined') return null;
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
  const parts = hostname.split('.');
  if (parts.length < 2) return null;
  const sub = parts[0].toLowerCase();
  // Superadmin console lives at admin.basiq360.tech — not a real tenant.
  if (sub === 'admin' || sub === 'www') return null;
  return sub;
}

export const isSudamaTenant = (): boolean => getTenantSubdomain() === 'sudama';


export interface AppConfig {
  // Environment
  env: 'development' | 'staging' | 'production';
  debug: boolean;

  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
    retries: number;
  };

  // Security Configuration
  security: {
    enableAuditLogs: boolean;
    enableRateLimiting: boolean;
    enableMFA: boolean;
    sessionTimeout: number; // in minutes
    tokenRefreshBuffer: number; // in seconds
    maxFailedLoginAttempts: number;
    failedLoginWindow: number; // in minutes
  };

  // Feature Flags
  features: {
    enableModuleSystem: boolean;
    enableFeatureFlags: boolean;
    enableAnalytics: boolean;
    enableNotifications: boolean;
    // Per-tenant: show a free-form "Thickness" field on product create/edit,
    // in the product listing, and in the Excel import template.
    // Enable only for tenants whose domain uses physical dimensions (e.g. Sudama — plywood).
    // Default: false (Oreno and other tenants do not see the field).
    // Toggle via env var REACT_APP_SHOW_PRODUCT_THICKNESS=true at build time.
    showProductThickness: boolean;
  };

  // Logging Configuration
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsole: boolean;
    enableRemote: boolean;
    enablePerformance: boolean;
  };

  // Performance Configuration
  performance: {
    enableLazyLoading: boolean;
    enableCodeSplitting: boolean;
    chunkSizeWarningLimit: number; // in KB
  };

  // UI Configuration
  ui: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    dateFormat: string;
    timeFormat: string;
    currency: string;
    timezone: string;
  };
}

// Default configuration
const defaultConfig: AppConfig = {
  env: (process.env.NODE_ENV as any) || 'development',
  debug: process.env.NODE_ENV === 'development',

  api: {
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8008/api',
    timeout: 30000, // 30 seconds
    retries: 3,
  },

  security: {
    enableAuditLogs: true,
    enableRateLimiting: true,
    enableMFA: process.env.REACT_APP_ENABLE_MFA === 'true',
    sessionTimeout: 30, // 30 minutes
    tokenRefreshBuffer: 300, // 5 minutes
    maxFailedLoginAttempts: 5,
    failedLoginWindow: 15, // 15 minutes
  },

  features: {
    enableModuleSystem: true,
    enableFeatureFlags: true,
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableNotifications: true,
    // Default OFF. Sudama's build sets REACT_APP_SHOW_PRODUCT_THICKNESS=true.
    showProductThickness: process.env.REACT_APP_SHOW_PRODUCT_THICKNESS === 'true',
  },

  logging: {
    level: process.env.NODE_ENV === 'production' ? 'warn' : 'debug',
    enableConsole: process.env.NODE_ENV !== 'production',
    enableRemote: process.env.NODE_ENV === 'production',
    enablePerformance: process.env.REACT_APP_ENABLE_PERFORMANCE === 'true',
  },

  performance: {
    enableLazyLoading: true,
    enableCodeSplitting: true,
    chunkSizeWarningLimit: 500, // 500 KB
  },

  ui: {
    theme: 'auto',
    language: 'en',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    timezone: 'UTC',
  },
};

// Get configuration
export const getConfig = (): AppConfig => {
  return defaultConfig;
};

// Get specific config value
export const getConfigValue = <K extends keyof AppConfig>(key: K): AppConfig[K] => {
  return defaultConfig[key];
};

// Check if development environment
export const isDevelopment = (): boolean => {
  return defaultConfig.env === 'development';
};

// Check if production environment
export const isProduction = (): boolean => {
  return defaultConfig.env === 'production';
};

// Check if staging environment
export const isStaging = (): boolean => {
  return defaultConfig.env === 'staging';
};

// Export default config
export default defaultConfig;
